import React, { useState, useEffect } from "react";
import { DollarSign, Ticket, Plus, Trash2, Pencil, Copy, MoreVertical } from "lucide-react";
import { TicketArchitectTabProps } from "./ComponentPropsV3";

const TicketArchitectTabV3: React.FC<TicketArchitectTabProps> = ({
  builderMode,
  feePreference,
  setFeePreference,
  tiers,
  handleOpenTicketDrawer,
  handleDuplicateTicket,
  handleRemoveTicket,
  createTierFromPreset,
}) => {
  const [activeMenuIdx, setActiveMenuIdx] = useState<number | null>(null);

  useEffect(() => {
    const handleClickOutside = () => setActiveMenuIdx(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Fee Defaults (Only in Full mode) */}
      {builderMode === "full" && (
        <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="v3-section-head">
            <DollarSign size={14} color="#EE3F7C" /> Ticketing Fee Defaults
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <button
              type="button"
              onClick={() => setFeePreference("Attendee Pays")}
              style={{
                padding: 14,
                borderRadius: 12,
                border: `1px solid ${feePreference === "Attendee Pays" ? "#EE3F7C" : "rgba(255,255,255,0.07)"}`,
                background: feePreference === "Attendee Pays" ? "rgba(238,63,124,0.12)" : "rgba(255,255,255,0.03)",
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Pass Fees to Attendees</div>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", display: "block", marginTop: 4 }}>
                Ticketing service fees are added on top of your set ticket price at checkout.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setFeePreference("Organizer Pays")}
              style={{
                padding: 14,
                borderRadius: 12,
                border: `1px solid ${feePreference === "Organizer Pays" ? "#EE3F7C" : "rgba(255,255,255,0.07)"}`,
                background: feePreference === "Organizer Pays" ? "rgba(238,63,124,0.12)" : "rgba(255,255,255,0.03)",
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Absorb Fees into Ticket Price</div>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", display: "block", marginTop: 4 }}>
                The listed ticket price is final for attendees; platform fees are deducted from your payout.
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Ticket Tiers */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="v3-section-head">
              <Ticket size={14} color="#EE3F7C" /> Ticket Tiers ({tiers.length})
            </div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "4px 0 0 0" }}>
              Configure single entry, VIP, or group packages.
            </p>
          </div>
          <button
            type="button"
            className="v3-btn-primary"
            style={{ fontSize: 12, padding: "8px 16px" }}
            onClick={() => handleOpenTicketDrawer()}
          >
            <Plus size={14} /> Create Ticket Tier
          </button>
        </div>

        {/* Quick Tier Presets */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 12 }}>
          <span style={{ fontSize: 10, fontFamily: "monospace", color: "rgba(255,255,255,0.4)", textTransform: "uppercase", alignSelf: "center", marginRight: 4 }}>Quick Add Preset:</span>
          <button type="button" className="v3-btn-ghost" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => createTierFromPreset("Free Pass", 0, "Individual", undefined, "Complimentary general admission pass.")}>+ Free Pass</button>
          <button type="button" className="v3-btn-ghost" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => createTierFromPreset("Early Bird", 25, "Individual", undefined, "Discounted early reservation ticket.")}>+ Early Bird ($25)</button>
          <button type="button" className="v3-btn-ghost" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => createTierFromPreset("General Admission", 50, "Individual", undefined, "Standard event admission entry.")}>+ General ($50)</button>
          <button type="button" className="v3-btn-ghost" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => createTierFromPreset("VIP Experience", 150, "Individual", undefined, "VIP access with front seating and perks.")}>+ VIP ($150)</button>
          <button type="button" className="v3-btn-ghost" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => createTierFromPreset("Squad Pack (4 Tickets)", 160, "Group", 4, "Group bundle entry for up to 4 guests.")}>+ Group Pack (4x)</button>
        </div>

        {/* Tiers List */}
        {tiers.length === 0 ? (
          <div style={{ padding: "32px 16px", textAlign: "center", borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)" }}>
            <Ticket size={28} color="rgba(255,255,255,0.2)" style={{ margin: "0 auto 8px auto", display: "block" }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>No ticket tiers created yet</div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 4, marginBottom: 12 }}>Click above to create custom tickets or select a preset.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tiers.map((tier, idx) => (
              <div
                key={tier.id}
                style={{
                  padding: 16,
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      width: 96,
                      height: 40,
                      borderRadius: 10,
                      background: tier.price === 0
                        ? "linear-gradient(135deg, #059669, #34D399)"
                        : "linear-gradient(135deg, #EE3F7C, #F79B59)",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 13,
                      letterSpacing: "0.02em",
                      whiteSpace: "nowrap",
                      boxShadow: tier.price === 0
                        ? "0 4px 14px rgba(52, 211, 153, 0.25)"
                        : "0 4px 14px rgba(238, 63, 124, 0.3)",
                      flexShrink: 0,
                    }}
                  >
                    {tier.price === 0 ? "FREE" : `$${tier.price}`}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{tier.name}</span>
                      <span style={{ fontSize: 9, fontFamily: "monospace", padding: "2px 6px", borderRadius: 4, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", textTransform: "uppercase" }}>
                        {tier.type}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>
                      Capacity: <strong>{tier.capacity}</strong> {tier.type === "Group" && `(Bundle of ${tier.groupSize || 4})`}
                      {tier.description && ` · ${tier.description}`}
                    </div>
                  </div>
                </div>

                <div style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenuIdx(activeMenuIdx === idx ? null : idx);
                    }}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: activeMenuIdx === idx ? "rgba(255, 255, 255, 0.12)" : "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 0.8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    title="More actions"
                  >
                    <MoreVertical size={16} />
                  </button>

                  {activeMenuIdx === idx && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 40,
                        zIndex: 100,
                        minWidth: 140,
                        padding: "6px",
                        borderRadius: 12,
                        background: "#181320",
                        border: "1px solid rgba(255, 255, 255, 0.12)",
                        boxShadow: "0 10px 28px rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuIdx(null);
                          handleOpenTicketDrawer(idx);
                        }}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: "transparent",
                          border: "none",
                          color: "#ffffff",
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign: "left",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          transition: "background 0.15s ease",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <Pencil size={13} color="rgba(255, 255, 255, 0.7)" /> Edit Ticket
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuIdx(null);
                          handleDuplicateTicket(idx);
                        }}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: "transparent",
                          border: "none",
                          color: "#ffffff",
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign: "left",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          transition: "background 0.15s ease",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <Copy size={13} color="rgba(255, 255, 255, 0.7)" /> Duplicate
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuIdx(null);
                          handleRemoveTicket(idx);
                        }}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: "transparent",
                          border: "none",
                          color: "#FF5D73",
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign: "left",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          transition: "background 0.15s ease",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255, 93, 115, 0.12)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <Trash2 size={13} color="#FF5D73" /> Delete Ticket
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketArchitectTabV3;
